import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Table,
  Input,
  Space,
  Tag,
  Select,
  Button,
  Upload,
  notification,
} from "antd";
import dayjs from "dayjs";
import { postReq } from "../api";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import AddOutreachModal from "../components/addOutreachModel";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

// 🔵🟡🟠🔴🟢 Stage colors
const stageColorMap = {
  INTRO: "blue",
  FU1: "gold",
  FU2: "orange",
  FU3: "red",
  REPLIED: "green",
};
const statusColorMap = {
  Pending: "orange",
  IN_DISCUSSION: "green",
  REPLIED: "blue",
  NOT_INTERESTED: "red",
  NO_REPLY: "gold",
};

const summaryStages = [
  { key: "TOTAL", label: "Total" },
  //   { key: "EMAIL", label: "Intro" },
  { key: "INTRO", label: "Intro" },
  { key: "FU1", label: "FU1" },
  { key: "FU2", label: "FU2" },
  { key: "FU3", label: "FU3" },
  { key: "REPLIED", label: "Replied" },
];

export default function OutreachTablePage() {
  const history = useHistory();
  const [tableData, setTableData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState(null);
  const [selectedStage, setSelectedStage] = useState("TOTAL");
  const [search, setSearch] = useState("");

  // 📡 Fetch summary counts
  const fetchSummary = async () => {
    const res = await postReq("common/outreach/summary/all");

    setSummary(res.data);
  };

  // 📡 Fetch table data
  const fetchTableData = async () => {
    setLoading(false);

    const params = new URLSearchParams();
    if (selectedStage !== "TOTAL") params.append("stage", selectedStage);
    if (search) params.append("search", search);

    const res = await postReq(`common/outreach/table?${params.toString()}`);

    setTableData(res.data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchSummary();
    fetchTableData();
  }, [selectedStage, search]);

  // 🔔 Follow-up chip
  const renderFollowUpChip = (date) => {
    if (!date) return "-";

    const diff = dayjs(date).diff(dayjs(), "day");

    if (diff < 0) return <Tag color="red">Overdue</Tag>;
    if (diff === 0) return <Tag color="gold">Today</Tag>;
    return <Tag color="blue">{diff}d to go</Tag>;
  };

  // 📊 Table columns
  const columns = [
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button
          type="link"
          onClick={(e) => {
            e.stopPropagation();
            history.push(`/company/spoc`);
          }}
        >
          View
        </Button>
      ),
    },
    {
      title: "Company / SPOC",
      dataIndex: "companyName",
      render: (_, record) => (
        <>
          <strong>{record.companyName}</strong>
          <div style={{ fontSize: 12, color: "#666" }}>{record.spocName}</div>
        </>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Stage",
      dataIndex: "currentStage",
      render: (stage) => <Tag color={stageColorMap[stage]}>{stage}</Tag>,
    },
    {
      title: "Last Contacted",
      dataIndex: "lastActionDate",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Follow-up",
      dataIndex: "nextFollowUpDate",
      render: renderFollowUpChip,
    },
    {
      title: "Touches",
      dataIndex: "totalTouches",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => <Tag color={statusColorMap[status]}>{status}</Tag>,
    },
    {
      title: "Owner",
      dataIndex: "ownerName",
    },
  ];

  const handleUploadExcel = async (file) => {
    const formData = new FormData();
    formData.append("excel", file);
    const res = await postReq("common/upload_spoc_bulk", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    if (res.status == 1) {
      notification.success({
        message: "Success",
        description: `successful.`,
        placement: "topRight",
      });
    } else {
      notification.success({
        message: "Error",
        description: `error.`,
        placement: "topRight",
      });
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {/* 🟦 Page Header */}
      <Title level={3}>Outreach</Title>

      {/* 🟩 Summary Bar */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {summaryStages.map((item) => (
          <Col key={item.key}>
            <Card
              hoverable
              onClick={() => setSelectedStage(item.key)}
              style={{
                width: 140,
                border:
                  selectedStage === item.key
                    ? "2px solid #1677ff"
                    : "1px solid #f0f0f0",
              }}
            >
              <Text type="secondary">{item.label}</Text>
              <Title level={3} style={{ margin: 0 }}>
                {summary[item.key] || 0}
              </Title>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 🔍 Filters Row */}
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Input.Search
          placeholder="Search by name or email"
          allowClear
          style={{ width: 300 }}
          onSearch={(val) => setSearch(val)}
        />

        <Space>
          <Select
            defaultValue="last_contacted"
            style={{ width: 180 }}
            options={[{ value: "last_contacted", label: "Last contacted" }]}
          />
          <Button onClick={fetchTableData}>Refresh</Button>

          <Upload
            accept=".csv,.xls,.xlsx"
            fileList={fileList}
            maxCount={1}
            beforeUpload={() => false} // prevent auto upload
            onChange={(info) => {
              const newFileList = info.fileList.slice(-1);
              setFileList(newFileList);

              const file = newFileList[0]?.originFileObj;
              if (file) {
                handleUploadExcel(file);
              }
            }}
          >
            <Button icon={<UploadOutlined />}>Upload Excel</Button>
          </Upload>
        </Space>
      </Space>

      {/* 📋 Main Table */}
      <Table
        rowKey="spocId"
        loading={loading}
        columns={columns}
        dataSource={tableData}
        pagination={{ pageSize: 10 }}
        onRow={(record) => ({
          onClick: () => {
            console.log("Open SPOC timeline:", record.spocId);
          },
        })}
      />
    </div>
  );
}
